import React from "react";
import { siteConfig } from "../../config/siteConfig";
import Eyebrow from "../ui/Eyebrow";
import Icon from "../ui/Icon";
import Reveal from "../ui/Reveal";

/**
 * Features — section « caractéristiques » premium « Edge-Lit Glass Card ».
 *
 * Contrat préservé (DESIGN-SPEC §4) : composant SANS prop, export par défaut,
 * lit `siteConfig.features` ({ sectionEyebrow, sectionTitle, sectionSubtitle,
 * items:[{ title, description, color, iconKey, iconPath }] }). 100 % config-driven.
 *
 * Concept gagnant (juré 1) + détails must-adopt (jurés 1/2/3/4) synthétisés en
 * UNE carte cohérente autour d'UNE direction de lumière (du haut) :
 *  - élévation directionnelle « lumière du haut » (lip blanc inset + ombre indigo
 *    qui tombe vers le bas au survol, sur la même transition que le lift -4px) ;
 *  - tuile-icône « cabochon de verre » (socle indigo-50 + chip ${color} bevelé) ;
 *  - hairline d'arête indigo->sky net (mask-XOR, 0 layout shift) ;
 *  - dot-grid indigo masqué en bas (matière, lisibilité intacte) ;
 *  - numéral Fraunces tabular-nums aligné en items-baseline sur le titre ;
 *  - typo 3 paliers verrouillés (numéral indigo/70 -> titre slate-900 -> corps slate-600) ;
 *  - spotlight curseur stateless (CSS var, 0 re-render) + sheen one-shot.
 *
 * Perf / a11y : 0 dépendance, tout en CSS build-time ; icônes aria-hidden ;
 * <h2> section / <h3> carte ; tout mouvement neutralisé sous prefers-reduced-motion
 * ET pointer:coarse (double verrou JS + CSS). Recolor-safe : marque uniquement
 * via indigo/sky + rgb(79 70 229) ; ${feature.color} (config) pilote le chip.
 */

// Handler module-level, défini UNE seule fois (jamais recréé dans le .map) :
// écrit deux custom properties directement sur le nœud DOM -> zéro setState,
// zéro re-render React. Court-circuité au tactile pour ne rien calculer.
const handleGlow = (event) => {
  if (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  ) {
    return;
  }
  const el = event.currentTarget;
  const rect = el.getBoundingClientRect();
  el.style.setProperty("--mx", `${((event.clientX - rect.left) / rect.width) * 100}%`);
  el.style.setProperty("--my", `${((event.clientY - rect.top) / rect.height) * 100}%`);
};

const Features = () => {
  const { features } = siteConfig;

  return (
    <section id="features" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          {features.sectionEyebrow && (
            <Eyebrow className="justify-center">{features.sectionEyebrow}</Eyebrow>
          )}
          {/* Titre de section à encre pleine (pas de gradient-clip), DESIGN-SPEC §1.2 */}
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mt-4 mb-6 text-slate-900 [text-wrap:balance]">
            {features.sectionTitle}
          </h2>
          <p className="text-xl text-slate-600 [text-wrap:pretty]">
            {features.sectionSubtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.items.map((feature, index) => (
            <Reveal
              as="article"
              key={index}
              index={index}
              onMouseMove={handleGlow}
              className="feature-card feature-tex group relative isolate overflow-hidden rounded-2xl bg-white pt-7 px-7 pb-8 md:pt-8 md:px-8 md:pb-9 antialiased [font-feature-settings:'kern','liga']"
            >
              {/* COUCHES DÉCORATIVES (aria-hidden + pointer-events-none) ---------- */}
              {/* Spotlight curseur indigo (piloté par --mx/--my via setProperty) */}
              <span
                aria-hidden="true"
                data-cursor-glow
                className="feature-spotlight pointer-events-none absolute inset-0 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100"
              />
              {/* Hairline d'arête indigo->sky révélé au hover (mask-XOR, 1px net) */}
              <span
                aria-hidden="true"
                className="feature-edge pointer-events-none absolute inset-0 rounded-[inherit] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
              />
              {/* Sheen diagonal blanc (balaie une fois au hover, motion-safe) */}
              <span
                aria-hidden="true"
                className="feature-sheen pointer-events-none absolute inset-0 rounded-[inherit]"
              />

              {/* CONTENU réel (au-dessus des couches) ---------------------------- */}
              <div className="relative z-10">
                {/* Tuile-icône « cabochon de verre » : socle indigo-50 + chip coloré */}
                <div className="relative grid place-items-center w-14 h-14 rounded-2xl bg-indigo-50 ring-1 ring-inset ring-indigo-100 transition-[transform,box-shadow] duration-300 ease-out motion-safe:group-hover:-translate-y-px group-hover:ring-indigo-200 mb-6">
                  <span
                    className={`relative grid place-items-center w-11 h-11 rounded-[14px] overflow-hidden bg-gradient-to-br ${feature.color} text-white transition-transform duration-300 ease-out will-change-transform motion-safe:group-hover:scale-[1.03]`}
                    style={{
                      boxShadow:
                        "inset 0 1px 0 0 rgba(255,255,255,.45), inset 0 -1px 1px 0 rgba(79,70,229,.35), 0 6px 14px -4px rgba(79,70,229,.45)",
                    }}
                  >
                    {/* Reflet glass STATIQUE haut-gauche (galbe, survit reduced-motion) */}
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 rounded-[14px] [background:radial-gradient(120%_120%_at_22%_18%,rgba(255,255,255,.55)_0%,rgba(255,255,255,0)_42%)]"
                    />
                    {feature.iconKey ? (
                      <Icon
                        name={feature.iconKey}
                        className="relative w-6 h-6"
                      />
                    ) : feature.iconPath ? (
                      <svg
                        className="relative w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d={feature.iconPath}
                        />
                      </svg>
                    ) : null}
                  </span>
                </div>

                {/* Header : numéral Fraunces aligné baseline + titre <h3> */}
                <div className="flex items-baseline gap-3">
                  <span
                    aria-hidden="true"
                    className="font-display tabular-nums select-none text-base leading-none text-indigo-700/70 group-hover:text-indigo-700 transition-colors"
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-[1.0625rem] sm:text-lg font-semibold leading-snug tracking-[-0.014em] text-slate-900 [text-wrap:balance] group-hover:text-indigo-700 transition-colors">
                    {feature.title}
                  </h3>
                </div>

                <p className="mt-2 text-sm leading-relaxed text-slate-600 max-w-[46ch] [text-wrap:pretty]">
                  {feature.description}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
